import { createAction, props } from '@ngrx/store';
import { LocationTrackerErrorKey, LocationTrackerUserConfigState } from '../location-tracker.state';
import { CompanionPushEvent } from '../location-tracker.wire';

/** Dispatched by the location-tracker page's `ngOnInit`, in place of the old load action. */
export const openLocationTrackerPage = createAction('[LocationTracker] Page opened');

/**
 * Dispatched by the seed effect once the user store has settled, with the stored config converted
 * to state. Populates `draft` for the very first time — see `LocationTrackerEffects.seedLocationTrackerDraft$`
 * for why it waits for the user store to settle rather than merely load.
 */
export const locationTrackerDraftSeeded = createAction(
    '[LocationTracker] Draft seeded',
    props<{ config: LocationTrackerUserConfigState }>(),
);

/**
 * Dispatched by the location-tracker page on every form edit; carries the whole (still
 * unsaved) config. This is the page's write-through for the draft the user is editing.
 */
export const updateLocationTrackerUserConfig = createAction(
    '[LocationTracker] Update config',
    props<{ config: LocationTrackerUserConfigState }>(),
);

/** Dispatched by the location-tracker page when the user asks to look up their current position. */
export const requestPrivacyPerimeter = createAction('[LocationTracker] Request privacy perimeter');

/**
 * Dispatched by the geolocation effect with the RAW coordinates from the browser — the reducer
 * owns truncating them for privacy, so no caller has to remember to do it.
 */
export const privacyPerimeterResolved = createAction(
    '[LocationTracker] Privacy perimeter resolved',
    props<{ latitude: number; longitude: number }>(),
);

/** Dispatched by the geolocation effect when the lookup fails, or geolocation is unavailable at all. */
export const privacyPerimeterRequestFailed = createAction(
    '[LocationTracker] Privacy perimeter request failed',
    props<{ errorKey: LocationTrackerErrorKey }>(),
);

/** Dispatched by the location-tracker page when the user clears the previously set perimeter. */
export const clearPrivacyPerimeter = createAction('[LocationTracker] Clear privacy perimeter');

/**
 * Dispatched by the location-tracker page to save the current draft. No props: the save effect
 * reads the draft straight out of the slice via `selectLocationTrackerUserConfig`.
 */
export const saveLocationTrackerUserConfig = createAction('[LocationTracker] Save');

/**
 * Dispatched once the user-store write for the `LocationTracker` key has been accepted. Also the
 * trigger for `AppConfigEffects.refreshOnLocationTrackerSave$`, which re-reads the admin-configured
 * `LocationTracker` settings — that refresh is deliberately not awaited here.
 */
export const saveLocationTrackerUserConfigSuccess = createAction('[LocationTracker] Save success');

/** Dispatched when the user-store write for the `LocationTracker` key fails. */
export const saveLocationTrackerUserConfigFailed = createAction('[LocationTracker] Save failed');

/** Dispatched by the save-message timer effect once the confirmation/error has been shown long enough. */
export const clearLocationTrackerSaveMessage = createAction('[LocationTracker] Clear save message');

/**
 * Dispatched once a companion push has passed every gate (online, and — for `startOfWork` — the
 * dedupe and `shareLocation` checks). This single action drives two independent reactions: one
 * effect POSTs the push, and another writes the derived tracking state into the user store — see
 * `LocationTrackerCompanionPushEffects` for why those stay two separate effects instead of one.
 */
export const companionPushRequested = createAction(
    '[LocationTracker] Companion push requested',
    props<{ event: CompanionPushEvent; occurredAt: string; startOfWork?: string }>(),
);
